import { Box, BreadcrumbList, Button, Container, Flex, theme, Typography } from '@fe-toolkit/web-components/ssr'

import { ProgressSteps } from '@giffgaff/broadband-common'

import { customEvent } from '@fe-toolkit/eventing'

import { basePath } from '@/constants'

import { useEffectOnceWithCondition } from '@/hooks/useEffectOnceWithCondition'

import getChangePlanPreviewEventEntity from '@/utils/eventing/getChangePlanPreviewEventEntity'

import ProfilePageLoader from '@/components/molecules/ProfilePageLoader'

import ChangePlanComparison from '../components/ChangePlanComparison'
import ChangePlanNoContent from '../components/ChangePlanNoContent'
import ChangePlanNotification from '../components/ChangePlanNotification'
import ChangePlanPaymentDetails from '../components/ChangePlanPaymentDetails'
import { ChangePlanSummaryState } from '../types'
import { changePlanProgressSteps, highlight } from '../consts'
import formatPlanPaymentAttributes from '../utils/formatPlanPaymentAttributes'
import { BottomLineHeading, TopLineHeading } from './ChangePlanSummary.styled'

import type { Plan } from '@/api'

export type ChangePlanSummaryProps = {
  orderKey: string
  memberEmail?: string
  oldPlanId?: string
  plan?: Plan
} & ChangePlanSummaryState

export default function ChangePlanSummary({
  status,
  orderKey,
  memberEmail,
  oldPlanId,
  planChangePreview,
  ecomOrderSchedule,
  plan,
}: Readonly<ChangePlanSummaryProps>) {
  const newPlan = planChangePreview?.newPlan
  const oldPlan = planChangePreview?.oldPlan
  const hasPlans = !!newPlan

  const planInfo = formatPlanPaymentAttributes(planChangePreview, ecomOrderSchedule)

  useEffectOnceWithCondition(
    ([oldPlanId, planChangePreview]) => {
      customEvent('bb_change_plan_journey_impression', {
        contextType: 'change_plan_confirmation',
        currentPlanId: oldPlanId ?? '',
        journeyEnd: true,
        newPlan: getChangePlanPreviewEventEntity(planChangePreview),
      })

      customEvent('bb_change_plan_success', {
        currentPlanId: oldPlanId ?? '',
        newPlan: getChangePlanPreviewEventEntity(planChangePreview),
      })
    },
    ([_0, _1, status]) => status !== 'idle' && status !== 'loading',
    [oldPlanId, planChangePreview, status] as const,
  )

  return (
    <article>
      <Container
        as="section"
        backgroundColor="wildSand"
        sx={({ theme }) => ({
          padding: `${theme.spacing.medium} 0`,
          [theme.breakpoints.up('medium')]: {
            padding: `${theme.spacing.large} 0 ${theme.spacing.medium}`,
          },
        })}
      >
        <BreadcrumbList
          items={[
            {
              href: basePath,
              name: 'Account',
              onClick: () => {
                customEvent('gg_click', { contextType: 'account' })
              },
            },
            { name: 'Change plan' },
          ]}
        />
      </Container>
      <Box
        padding="fine"
        sx={({ theme }) => ({
          backgroundColor: theme.colors.yellow,
        })}
      >
        <Box
          sx={({ theme }) => ({
            margin: `${theme.spacing.medium} auto`,
            maxWidth: theme.layout.maxWidth,
            [theme.breakpoints.up('medium')]: {
              marginTop: theme.spacing.large,
            },
          })}
        >
          <ProgressSteps currentStep={3} steps={changePlanProgressSteps} variant="highlight"></ProgressSteps>
        </Box>

        <Box
          as="h1"
          sx={({ theme }) => ({
            marginBottom: theme.spacing.mediumLarge,
            marginTop: theme.spacing.medium,
            textAlign: 'center',
          })}
          testId="change-plan-summary-title"
        >
          <TopLineHeading>New plan</TopLineHeading>
          <BottomLineHeading>sorted</BottomLineHeading>
        </Box>
      </Box>

      <Container
        as="section"
        backgroundColor="wildSand"
        sx={({ theme }) => ({
          padding: `${theme.spacing.medium} 0`,
          [theme.breakpoints.up('medium')]: {
            padding: `${theme.spacing.large} 0 ${theme.spacing.medium}`,
          },
        })}
      >
        {status === 'loading' && <ProfilePageLoader />}

        {(status === 'empty' || status === 'fetch_failed') && (
          <ChangePlanNoContent>No plan change information available</ChangePlanNoContent>
        )}

        {status !== 'loading' && hasPlans && (
          <Flex flexDirection="column" gap={theme.spacing.medium}>
            <Flex flexDirection="column" gap={theme.spacing.small}>
              <Typography as="h2" data={{ orderKey }} variant="louder">
                Order #{orderKey}
              </Typography>
              {memberEmail && (
                <Typography as="p" variant="speak">
                  We’ve emailed confirmation to{' '}
                  <Typography as="span" variant="speakUp">
                    {memberEmail}
                  </Typography>
                </Typography>
              )}
            </Flex>
            <Flex flexDirection="column" gap={theme.spacing.medium} maxWidth={theme.layout.readableWidth}>
              <ChangePlanComparison newPlan={newPlan} oldPlan={oldPlan} plan={plan} />

              <ChangePlanPaymentDetails plan={plan} planInfo={planInfo} />

              <ChangePlanNotification>
                <Typography as="p" data={planInfo} variant="speakUp">
                  {highlight[planInfo.changeType].title}
                </Typography>
                <Typography as="p" data={planInfo} variant="speak">
                  {highlight[planInfo.changeType].copy}
                </Typography>
              </ChangePlanNotification>

              <Button
                href={basePath}
                sx={{ alignSelf: 'flex-start', marginBottom: theme.spacing.medium }}
                variant="standard"
                onClick={() => {
                  customEvent('bb_change_plan_journey_cta_click', {
                    contextType: 'change_plan_confirmation',
                    currentPlanId: oldPlanId ?? '',
                    newPlan: getChangePlanPreviewEventEntity(planChangePreview),
                  })
                }}
              >
                Back to my account
              </Button>
            </Flex>
          </Flex>
        )}
      </Container>
    </article>
  )
}
